import React, { useEffect, useRef, useState } from 'react';
import './Asteroids.css';

const AsteroidsGame = () => {
  const canvasRef = useRef(null);
  const gameOverRef = useRef(false);
  const [gameStarted, setGameStarted] = useState(false);
  
  // Draw start screen
  const drawStartScreen = () => {
    const canvas = canvasRef.current;
    if (!canvas) return; // Safety check
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas to full window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Clear canvas
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw title
    ctx.fillStyle = 'white';
    ctx.shadowColor = '#00ff00';
    ctx.shadowBlur = 10;
    ctx.font = '80px VT323, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('ASTEROIDS', canvas.width / 2, canvas.height / 3);
    
    // Draw instructions
    ctx.shadowBlur = 0;
    ctx.font = '30px VT323, monospace';
    ctx.fillText('PRESS ENTER TO START', canvas.width / 2, canvas.height / 2);
    
    // Draw controls
    ctx.font = '25px VT323, monospace';
    const lineHeight = 35;
    let lineY = canvas.height / 2 + 80;
    
    ctx.fillText('CONTROLS:', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('ARROW KEYS / WASD: MOVE SHIP', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('SPACE: FIRE (HOLD FOR RAPID FIRE)', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('R: RESTART WHEN GAME OVER', canvas.width / 2, lineY);
    
    // Draw features
    lineY += lineHeight * 1.5;
    ctx.fillText('FEATURES:', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('WATCH OUT FOR ALIEN SAUCERS!', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('COLLECT SPEED POWERUPS (CYAN LIGHTNING)', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('POWERUPS LAST FOR 20 SECONDS', canvas.width / 2, lineY);
  };
  
  // Component-level event listener for the R key
  useEffect(() => {
    const handleRestartKey = (event) => {
      if (event.key === 'r' && gameOverRef.current) {
        console.log("R key pressed at component level - reloading");
        window.location.reload();
      }
      
      // Handle Enter key to start game
      if (event.key === 'Enter' && !gameStarted) {
        setGameStarted(true);
      }
    };
    
    window.addEventListener('keydown', handleRestartKey);
    
    return () => {
      window.removeEventListener('keydown', handleRestartKey);
    };
  }, [gameStarted]);
  
  useEffect(() => {
    if (!gameStarted) return; // Don't initialize game until started
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas to full window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Game state
    let score = 0;
    let lives = 3;
    let level = 1;
    let gameOver = false;
    let asteroids = [];
    let bullets = [];
    let saucers = [];
    let saucerBullets = [];
    let powerups = [];
    let hasSpeedPowerup = false;
    
    // Floating text properties
    const floatingTexts = [
      { text: 'BBglobalsolutions.org', x: 0, y: 0, alpha: 0, speed: 1, color: '#00ff00', size: 30 },
      { text: 'BB Global Solutions', x: 0, y: 0, alpha: 0, speed: 1.2, color: '#00ffff', size: 35 },
      { text: 'The next generation of software solutions', x: 0, y: 0, alpha: 0, speed: 0.8, color: '#ff00ff', size: 25 },
      { text: 'Transforming Businesses with AI', x: 0, y: 0, alpha: 0, speed: 1.1, color: '#ffff00', size: 28 },
      { text: 'Join the AI Revolution', x: 0, y: 0, alpha: 0, speed: 0.9, color: '#ff8800', size: 32 },
      { text: 'Empowering Global Innovation', x: 0, y: 0, alpha: 0, speed: 1.3, color: '#00ff88', size: 27 },
      { text: 'The Future of Business is Here', x: 0, y: 0, alpha: 0, speed: 1.4, color: '#ff0088', size: 24 }
    ];
    
    // Keep gameOver state in ref for access outside this effect
    gameOverRef.current = gameOver;
    
    // Ship properties
    const ship = {
      x: canvas.width / 2,
      y: canvas.height / 2,
      radius: 10,
      angle: 0,
      rotation: 0,
      thrusting: false,
      thrust: {
        x: 0,
        y: 0
      },
      invulnerable: false,
      hasSpeedBoost: false,
      lastFireTime: 0 // Track when the ship last fired
    };
    
    // Game settings
    const FPS = 60;
    const FRICTION = 0.7;
    const SHIP_THRUST = 0.3;
    const SHIP_BOOST_MULTIPLIER = 2;
    const TURN_SPEED = 0.06;
    const BULLET_SPEED = 8;
    const BULLET_LIFE = 90;
    const MAX_BULLETS = 10;
    const ASTEROID_SPEED = 1;
    const ASTEROID_VERTICES = 10;
    const ASTEROID_JAGGEDNESS = 0.4;
    const ASTEROID_COUNT = Math.min(3 + level, 10);
    const SAUCER_SPEED = 2;
    const SAUCER_BULLET_SPEED = 4;
    const SAUCER_FIRE_RATE = 2000;
    const POWERUP_SIZE = 15;
    const POWERUP_DURATION = 20000;
    const FIRE_RATE = 250; // Time between shots when space is held (milliseconds)
    
    // Keyboard handlers
    const keyDown = {};
    
    // Direct key handler for restart
    const handleKeyDown = (e) => {
      keyDown[e.key] = true;
      
      // Special case for 'r' key to restart game when game over
      if (e.key === 'r' && gameOver) {
        console.log("R key pressed - restarting game");
        window.location.reload();
        return;
      }
    };
    
    const handleKeyUp = (e) => {
      keyDown[e.key] = false;
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    // Create saucer
    const createSaucer = () => {
      const side = Math.random() < 0.5 ? 0 : 1; // 0 for left, 1 for right
      const x = side === 0 ? 0 : canvas.width;
      const y = Math.random() * canvas.height;
      const direction = side === 0 ? 1 : -1;
      
      saucers.push({
        x,
        y,
        xv: SAUCER_SPEED * direction,
        yv: (Math.random() * 2 - 1) * SAUCER_SPEED / 2,
        radius: 15,
        lastShot: 0
      });
    };
    
    // Saucer fires a bullet
    const saucerFire = (saucer) => {
      // Calculate direction toward ship but with some randomness
      const dx = ship.x - saucer.x;
      const dy = ship.y - saucer.y;
      const angle = Math.atan2(dy, dx) + (Math.random() - 0.5) * 0.5; // Add some randomness
      
      saucerBullets.push({
        x: saucer.x,
        y: saucer.y,
        xv: SAUCER_BULLET_SPEED * Math.cos(angle),
        yv: SAUCER_BULLET_SPEED * Math.sin(angle),
        age: 0
      });
      
      saucer.lastShot = Date.now();
    };
    
    // Create power up
    const createPowerup = (x, y, type = 'speed') => {
      powerups.push({
        x,
        y,
        type,
        radius: POWERUP_SIZE,
        blinkOn: true,
        blinkTime: 0
      });
    };
    
    // Create asteroids
    const createAsteroids = () => {
      asteroids = [];
      let x, y;
      for (let i = 0; i < ASTEROID_COUNT; i++) {
        do {
          x = Math.random() * canvas.width;
          y = Math.random() * canvas.height;
        } while (Math.sqrt((x - ship.x) ** 2 + (y - ship.y) ** 2) < 100);
        
        const size = Math.ceil(Math.random() * 3);
        
        asteroids.push({
          x,
          y,
          xv: Math.random() * ASTEROID_SPEED * (Math.random() < 0.5 ? 1 : -1),
          yv: Math.random() * ASTEROID_SPEED * (Math.random() < 0.5 ? 1 : -1),
          radius: 50 / size,
          size: size,
          vertices: Math.floor(Math.random() * (ASTEROID_VERTICES + 1) + ASTEROID_VERTICES / 2),
          offsets: []
        });
        
        // Create the asteroid shape
        for (let j = 0; j < asteroids[i].vertices; j++) {
          asteroids[i].offsets.push(
            Math.random() * ASTEROID_JAGGEDNESS * 2 + 1 - ASTEROID_JAGGEDNESS
          );
        }
      }
      
      // Randomly decide if this level should spawn a saucer
      if (level > 1 && Math.random() < 0.7) {
        setTimeout(createSaucer, Math.random() * 10000 + 5000);
      }
    };
    
    // Create explosion particles - now a no-op (doesn't do anything)
    const createExplosion = () => {
      // Function left in place but empty to maintain compatibility with existing code
    };
    
    // Fire bullet
    const fireBullet = () => {
      // Create and add a new bullet to the array
      bullets.push({
        x: ship.x + 4/3 * ship.radius * Math.cos(ship.angle),
        y: ship.y - 4/3 * ship.radius * Math.sin(ship.angle),
        xv: BULLET_SPEED * Math.cos(ship.angle),
        yv: -BULLET_SPEED * Math.sin(ship.angle),
        age: 0
      });
    };
    
    // Reset the ship
    const resetShip = () => {
      ship.x = canvas.width / 2;
      ship.y = canvas.height / 2;
      ship.angle = 1.5 * Math.PI;
      ship.rotation = 0;
      ship.thrusting = false;
      ship.thrust.x = 0;
      ship.thrust.y = 0;
      ship.invulnerable = true;
      ship.hasSpeedBoost = false;
      
      setTimeout(() => {
        ship.invulnerable = false;
      }, 3000);
    };
    
    // Reset the game
    const resetGame = () => {
      gameOver = false;
      gameOverRef.current = false;
      level = 1;
      score = 0;
      lives = 3;
      saucers = [];
      saucerBullets = [];
      powerups = [];
      bullets = [];
      hasSpeedPowerup = false;
      
      resetShip();
      createAsteroids();
    };
    
    // Draw the ship
    const drawShip = () => {
      if (gameOver) return;
      
      const blinkRate = 0.1;
      if (ship.invulnerable && Math.floor(Date.now() / 100) % 2 === 0) return;
      
      // Change ship color when speed boost is active
      ctx.strokeStyle = ship.hasSpeedBoost ? '#00ffff' : 'white';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(
        ship.x + 4/3 * ship.radius * Math.cos(ship.angle),
        ship.y - 4/3 * ship.radius * Math.sin(ship.angle)
      );
      ctx.lineTo(
        ship.x - ship.radius * (2/3 * Math.cos(ship.angle) + Math.sin(ship.angle)),
        ship.y + ship.radius * (2/3 * Math.sin(ship.angle) - Math.cos(ship.angle))
      );
      ctx.lineTo(
        ship.x - ship.radius * (2/3 * Math.cos(ship.angle) - Math.sin(ship.angle)),
        ship.y + ship.radius * (2/3 * Math.sin(ship.angle) + Math.cos(ship.angle))
      );
      ctx.closePath();
      ctx.stroke();
      
      // Draw thruster
      if (ship.thrusting) {
        ctx.fillStyle = ship.hasSpeedBoost ? '#00ffaa' : 'red';
        ctx.beginPath();
        ctx.moveTo(
          ship.x - ship.radius * (2/3 * Math.cos(ship.angle) + 0.5 * Math.sin(ship.angle)),
          ship.y + ship.radius * (2/3 * Math.sin(ship.angle) - 0.5 * Math.cos(ship.angle))
        );
        ctx.lineTo(
          ship.x - ship.radius * (5/3 * Math.cos(ship.angle)),
          ship.y + ship.radius * (5/3 * Math.sin(ship.angle))
        );
        ctx.lineTo(
          ship.x - ship.radius * (2/3 * Math.cos(ship.angle) - 0.5 * Math.sin(ship.angle)),
          ship.y + ship.radius * (2/3 * Math.sin(ship.angle) + 0.5 * Math.cos(ship.angle))
        );
        ctx.closePath();
        ctx.fill();
      }
    };
    
    // Draw saucers
    const drawSaucers = () => {
      ctx.strokeStyle = '#FF5555';
      ctx.lineWidth = 2;
      
      for (let i = 0; i < saucers.length; i++) {
        const saucer = saucers[i];
        
        // Draw saucer body
        ctx.beginPath();
        ctx.ellipse(saucer.x, saucer.y, saucer.radius, saucer.radius / 2, 0, 0, Math.PI * 2);
        ctx.stroke();
        
        // Draw cockpit
        ctx.beginPath();
        ctx.ellipse(saucer.x, saucer.y - saucer.radius / 3, saucer.radius / 2, saucer.radius / 4, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    };
    
    // Draw saucer bullets
    const drawSaucerBullets = () => {
      ctx.fillStyle = '#FF5555';
      for (let i = 0; i < saucerBullets.length; i++) {
        ctx.beginPath();
        ctx.arc(saucerBullets[i].x, saucerBullets[i].y, 2, 0, Math.PI * 2, false);
        ctx.fill();
      }
    };
    
    // Draw power ups
    const drawPowerups = () => {
      for (let i = 0; i < powerups.length; i++) {
        const powerup = powerups[i];
        
        // Blink effect
        if (Date.now() - powerup.blinkTime > 300) {
          powerup.blinkOn = !powerup.blinkOn;
          powerup.blinkTime = Date.now();
        }
        
        if (!powerup.blinkOn) continue;
        
        ctx.strokeStyle = '#00FFFF';
        ctx.fillStyle = 'rgba(0, 255, 255, 0.3)';
        ctx.lineWidth = 2;
        
        if (powerup.type === 'speed') {
          // Draw lightning bolt for speed powerup
          ctx.beginPath();
          ctx.moveTo(powerup.x - powerup.radius / 2, powerup.y - powerup.radius);
          ctx.lineTo(powerup.x + powerup.radius / 3, powerup.y - powerup.radius / 3);
          ctx.lineTo(powerup.x - powerup.radius / 3, powerup.y + powerup.radius / 3);
          ctx.lineTo(powerup.x + powerup.radius / 2, powerup.y + powerup.radius);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }
      }
    };
    
    // Draw asteroids
    const drawAsteroids = () => {
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      
      for (let i = 0; i < asteroids.length; i++) {
        const asteroid = asteroids[i];
        
        // Draw asteroid
        ctx.beginPath();
        const angle = Math.PI * 2 / asteroid.vertices;
        for (let j = 0; j < asteroid.vertices; j++) {
          const radius = asteroid.radius * (1 + asteroid.offsets[j]);
          ctx.lineTo(
            asteroid.x + radius * Math.cos(angle * j),
            asteroid.y + radius * Math.sin(angle * j)
          );
        }
        ctx.closePath();
        ctx.stroke();
      }
    };
    
    // Draw bullets
    const drawBullets = () => {
      ctx.fillStyle = 'white';
      for (let i = 0; i < bullets.length; i++) {
        ctx.beginPath();
        ctx.arc(bullets[i].x, bullets[i].y, 2, 0, Math.PI * 2, false);
        ctx.fill();
      }
    };
    
    // Draw score and lives
    const drawHUD = () => {
      ctx.fillStyle = 'white';
      ctx.font = '20px VT323, monospace';
      ctx.fillText(`SCORE: ${score}`, 20, 30);
      ctx.fillText(`LEVEL: ${level}`, 20, 60);
      
      // Draw special status
      if (ship.hasSpeedBoost) {
        ctx.fillStyle = '#00FFFF';
        ctx.fillText('SPEED BOOST ACTIVE', 20, 90);
      }
      
      // Draw lives
      for (let i = 0; i < lives; i++) {
        ctx.beginPath();
        ctx.moveTo(canvas.width - 20 - i * 25, 30);
        ctx.lineTo(canvas.width - 30 - i * 25, 20);
        ctx.lineTo(canvas.width - 40 - i * 25, 30);
        ctx.lineTo(canvas.width - 30 - i * 25, 25);
        ctx.closePath();
        ctx.stroke();
      }
    };
    
    // Game over screen
    const drawGameOver = () => {
      // Log game over state for debugging
      console.log("Drawing game over screen, gameOver =", gameOver);
      
      // Semi-transparent background for better text visibility
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Game over text with glow effect
      ctx.fillStyle = 'white';
      ctx.shadowColor = 'red';
      ctx.shadowBlur = 15;
      ctx.font = '70px VT323, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('GAME OVER', canvas.width / 2, canvas.height / 2 - 50);
      
      // Reset shadow for other text
      ctx.shadowBlur = 0;
      ctx.font = '35px VT323, monospace';
      ctx.fillText(`FINAL SCORE: ${score}`, canvas.width / 2, canvas.height / 2 + 10);
      ctx.fillText('PRESS R TO RESTART', canvas.width / 2, canvas.height / 2 + 60);
      
      // Draw a restart button as a fallback option
      const buttonX = canvas.width / 2;
      const buttonY = canvas.height / 2 + 120;
      const buttonWidth = 200;
      const buttonHeight = 50;
      
      // Button background
      ctx.fillStyle = '#4CAF50';
      ctx.fillRect(buttonX - buttonWidth/2, buttonY - buttonHeight/2, buttonWidth, buttonHeight);
      
      // Button text
      ctx.fillStyle = 'white';
      ctx.font = '25px VT323, monospace';
      ctx.fillText('RESTART GAME', buttonX, buttonY + 8);
      
      // Add click handler for the button
      canvas.onclick = (e) => {
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        if (
          mouseX >= buttonX - buttonWidth/2 &&
          mouseX <= buttonX + buttonWidth/2 &&
          mouseY >= buttonY - buttonHeight/2 &&
          mouseY <= buttonY + buttonHeight/2
        ) {
          console.log("Restart button clicked");
          window.location.reload();
        }
      };
      
      ctx.textAlign = 'left';
    };
    
    // Check if a point is inside an asteroid's outline
    const isPointInAsteroid = (x, y, asteroid) => {
      const angle = Math.PI * 2 / asteroid.vertices;
      const points = [];
      
      // Get all vertices of the asteroid
      for (let i = 0; i < asteroid.vertices; i++) {
        const radius = asteroid.radius * (1 + asteroid.offsets[i]);
        points.push({
          x: asteroid.x + radius * Math.cos(angle * i),
          y: asteroid.y + radius * Math.sin(angle * i)
        });
      }
      
      // Check if point is inside the polygon
      let inside = false;
      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const xi = points[i].x, yi = points[i].y;
        const xj = points[j].x, yj = points[j].y;
        
        const intersect = ((yi > y) !== (yj > y)) && 
            (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      
      return inside;
    };
    
    // Check if the ship's outline intersects with an asteroid's outline
    const checkShipAsteroidCollision = (ship, asteroid) => {
      // Ship points (the triangle outline)
      const shipPoints = [
        {
          x: ship.x + 4/3 * ship.radius * Math.cos(ship.angle),
          y: ship.y - 4/3 * ship.radius * Math.sin(ship.angle)
        },
        {
          x: ship.x - ship.radius * (2/3 * Math.cos(ship.angle) + Math.sin(ship.angle)),
          y: ship.y + ship.radius * (2/3 * Math.sin(ship.angle) - Math.cos(ship.angle))
        },
        {
          x: ship.x - ship.radius * (2/3 * Math.cos(ship.angle) - Math.sin(ship.angle)),
          y: ship.y + ship.radius * (2/3 * Math.sin(ship.angle) + Math.cos(ship.angle))
        }
      ];
      
      // Asteroid points
      const angle = Math.PI * 2 / asteroid.vertices;
      const asteroidPoints = [];
      
      for (let i = 0; i < asteroid.vertices; i++) {
        const radius = asteroid.radius * (1 + asteroid.offsets[i]);
        asteroidPoints.push({
          x: asteroid.x + radius * Math.cos(angle * i),
          y: asteroid.y + radius * Math.sin(angle * i)
        });
      }
      
      // Check if any ship point is inside the asteroid
      for (const point of shipPoints) {
        if (isPointInAsteroid(point.x, point.y, asteroid)) {
          return true;
        }
      }
      
      // Check if any asteroid point is inside the ship (using line intersections)
      for (const point of asteroidPoints) {
        // Check if point is inside the ship triangle
        const p0 = shipPoints[0], p1 = shipPoints[1], p2 = shipPoints[2];
        const A = 1/2 * (-p1.y * p2.x + p0.y * (-p1.x + p2.x) + p0.x * (p1.y - p2.y) + p1.x * p2.y);
        const sign = A < 0 ? -1 : 1;
        const s = (p0.y * p2.x - p0.x * p2.y + (p2.y - p0.y) * point.x + (p0.x - p2.x) * point.y) * sign;
        const t = (p0.x * p1.y - p0.y * p1.x + (p0.y - p1.y) * point.x + (p1.x - p0.x) * point.y) * sign;
        
        if (s > 0 && t > 0 && (s + t) < 2 * A * sign) {
          return true;
        }
      }
      
      // Check line segment intersections between ship and asteroid
      for (let i = 0; i < shipPoints.length; i++) {
        const shipP1 = shipPoints[i];
        const shipP2 = shipPoints[(i + 1) % shipPoints.length];
        
        for (let j = 0; j < asteroidPoints.length; j++) {
          const asteroidP1 = asteroidPoints[j];
          const asteroidP2 = asteroidPoints[(j + 1) % asteroidPoints.length];
          
          // Check if these line segments intersect
          if (linesIntersect(
            shipP1.x, shipP1.y, shipP2.x, shipP2.y,
            asteroidP1.x, asteroidP1.y, asteroidP2.x, asteroidP2.y
          )) {
            return true;
          }
        }
      }
      
      return false;
    };
    
    // Check if two line segments intersect
    const linesIntersect = (x1, y1, x2, y2, x3, y3, x4, y4) => {
      // Calculate the direction of the lines
      const uA = ((x4-x3)*(y1-y3) - (y4-y3)*(x1-x3)) / ((y4-y3)*(x2-x1) - (x4-x3)*(y2-y1));
      const uB = ((x2-x1)*(y1-y3) - (y2-y1)*(x1-x3)) / ((y4-y3)*(x2-x1) - (x4-x3)*(y2-y1));
      
      // If uA and uB are between 0-1, lines are colliding
      return (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1);
    };
    
    // Check for collisions
    const checkCollisions = () => {
      // Ship and asteroids
      if (!ship.invulnerable) {
        for (let i = 0; i < asteroids.length; i++) {
          // Use the precise outline collision detection
          if (checkShipAsteroidCollision(ship, asteroids[i])) {
            createExplosion();
            lives--;
            
            if (lives <= 0) {
              gameOver = true;
              gameOverRef.current = true; // Update ref with game over state
              console.log("Game over state set to true");
            } else {
              resetShip();
            }
            break;
          }
        }
        
        // Ship and saucer bullets
        for (let i = saucerBullets.length - 1; i >= 0; i--) {
          if (
            Math.sqrt((ship.x - saucerBullets[i].x) ** 2 + (ship.y - saucerBullets[i].y) ** 2) <
            ship.radius
          ) {
            createExplosion();
            saucerBullets.splice(i, 1);
            lives--;
            
            if (lives <= 0) {
              gameOver = true;
              gameOverRef.current = true; // Update ref with game over state
              console.log("Game over state set to true");
            } else {
              resetShip();
            }
            break;
          }
        }
        
        // Ship and saucers
        for (let i = 0; i < saucers.length; i++) {
          if (
            Math.sqrt((ship.x - saucers[i].x) ** 2 + (ship.y - saucers[i].y) ** 2) <
            ship.radius + saucers[i].radius
          ) {
            createExplosion();
            createExplosion(saucers[i].x, saucers[i].y, '#FF5555');
            saucers.splice(i, 1);
            lives--;
            
            if (lives <= 0) {
              gameOver = true;
              gameOverRef.current = true; // Update ref with game over state
              console.log("Game over state set to true");
            } else {
              resetShip();
            }
            break;
          }
        }
      }
      
      // Ship and powerups
      for (let i = powerups.length - 1; i >= 0; i--) {
        if (
          Math.sqrt((ship.x - powerups[i].x) ** 2 + (ship.y - powerups[i].y) ** 2) <
          ship.radius + powerups[i].radius
        ) {
          if (powerups[i].type === 'speed') {
            ship.hasSpeedBoost = true;
            
            // Speed boost lasts for 10 seconds
            setTimeout(() => {
              ship.hasSpeedBoost = false;
            }, POWERUP_DURATION);
          }
          
          powerups.splice(i, 1);
          break;
        }
      }
      
      // Bullets and asteroids
      for (let i = bullets.length - 1; i >= 0; i--) {
        // Check collisions with asteroids
        for (let j = asteroids.length - 1; j >= 0; j--) {
          // Improved collision detection using asteroid outline
          const dx = bullets[i].x - asteroids[j].x;
          const dy = bullets[i].y - asteroids[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // First do a fast distance check (optimization)
          if (distance < asteroids[j].radius * 1.5) {
            // Initialize collision flag here
            let collision = false;
            
            // Try simple radius check first - if bullet is well inside asteroid, it's definitely a hit
            if (distance < asteroids[j].radius * 0.8) {
              collision = true;
            } else {
              // For bullets, we'll use a simpler approach - check if the bullet
              // is close to any edge of the asteroid polygon
              const angle = Math.PI * 2 / asteroids[j].vertices;
              const points = [];
              
              // Get all vertices of the asteroid
              for (let k = 0; k < asteroids[j].vertices; k++) {
                const radius = asteroids[j].radius * (1 + asteroids[j].offsets[k]);
                points.push({
                  x: asteroids[j].x + radius * Math.cos(angle * k),
                  y: asteroids[j].y + radius * Math.sin(angle * k)
                });
              }
              
              // Check if bullet is close to any vertex of the asteroid
              for (let k = 0; k < points.length; k++) {
                const vertexDist = Math.sqrt(
                  (bullets[i].x - points[k].x) ** 2 + 
                  (bullets[i].y - points[k].y) ** 2
                );
                
                // If bullet is close to any vertex
                if (vertexDist < 10) { // 10 pixels threshold for vertices
                  collision = true;
                  break;
                }
              }
              
              // If no vertex collision, check edges
              if (!collision) {
                // Check if bullet is close to any edge of the asteroid
                for (let k = 0; k < points.length; k++) {
                  const p1 = points[k];
                  const p2 = points[(k + 1) % points.length];
                  
                  // Distance from point to line segment
                  const lineLength = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
                  const projection = ((bullets[i].x - p1.x) * (p2.x - p1.x) + 
                                    (bullets[i].y - p1.y) * (p2.y - p1.y)) / 
                                    (lineLength * lineLength);
                  
                  let closestX, closestY;
                  if (projection < 0) {
                    closestX = p1.x;
                    closestY = p1.y;
                  } else if (projection > 1) {
                    closestX = p2.x;
                    closestY = p2.y;
                  } else {
                    closestX = p1.x + projection * (p2.x - p1.x);
                    closestY = p1.y + projection * (p2.y - p1.y);
                  }
                  
                  const distToEdge = Math.sqrt((bullets[i].x - closestX) ** 2 + 
                                              (bullets[i].y - closestY) ** 2);
                  
                  // If bullet is close enough to edge, count as collision
                  // Increased from 5 to 12 pixels threshold
                  if (distToEdge < 12) {
                    collision = true;
                    break;
                  }
                }
              }
              
              // Check if bullet is inside the asteroid polygon (fallback check)
              if (!collision && isPointInAsteroid(bullets[i].x, bullets[i].y, asteroids[j])) {
                collision = true;
              }
            }
            
            if (collision) {
              // Add score based on asteroid size
              score += 100 * asteroids[j].size;
              
              // Create explosion
              createExplosion();
              
              // Randomly create a powerup (only if none exists and only from large asteroids)
              if (powerups.length === 0 && asteroids[j].size === 1 && Math.random() < 0.2) {
                createPowerup(asteroids[j].x, asteroids[j].y);
              }
              
              // Split asteroid
              if (asteroids[j].size < 3) {
                for (let k = 0; k < 2; k++) {
                  asteroids.push({
                    x: asteroids[j].x,
                    y: asteroids[j].y,
                    xv: Math.random() * ASTEROID_SPEED * 2 - ASTEROID_SPEED,
                    yv: Math.random() * ASTEROID_SPEED * 2 - ASTEROID_SPEED,
                    radius: asteroids[j].radius / 2,
                    size: asteroids[j].size + 1,
                    vertices: Math.floor(Math.random() * (ASTEROID_VERTICES + 1) + ASTEROID_VERTICES / 2),
                    offsets: []
                  });
                  
                  // Create the asteroid shape
                  for (let l = 0; l < asteroids[asteroids.length - 1].vertices; l++) {
                    asteroids[asteroids.length - 1].offsets.push(
                      Math.random() * ASTEROID_JAGGEDNESS * 2 + 1 - ASTEROID_JAGGEDNESS
                    );
                  }
                }
              }
              
              // Remove the asteroid and bullet
              asteroids.splice(j, 1);
              bullets.splice(i, 1);
              
              break;
            }
          }
        }
        
        // If bullet was removed, skip rest of the loop
        if (i >= bullets.length) continue;
        
        // Check collisions with saucers
        for (let j = saucers.length - 1; j >= 0; j--) {
          // Precise collision detection
          const dx = bullets[i].x - saucers[j].x;
          const dy = bullets[i].y - saucers[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Use exact saucer radius for precise collision
          if (distance < saucers[j].radius) {
            // Add score
            score += 500;
            
            // Create explosion
            createExplosion();
            
            // Create powerup from saucer (guaranteed)
            if (powerups.length === 0) {
              createPowerup(saucers[j].x, saucers[j].y);
            }
            
            // Remove the saucer and bullet
            saucers.splice(j, 1);
            bullets.splice(i, 1);
            
            break;
          }
        }
      }
      
      // Check if level is complete
      if (asteroids.length === 0) {
        level++;
        createAsteroids();
      }
    };
    
    // Update ship
    const updateShip = () => {
      // Rotate ship
      if (keyDown['ArrowLeft'] || keyDown['a']) {
        ship.rotation = TURN_SPEED;
      } else if (keyDown['ArrowRight'] || keyDown['d']) {
        ship.rotation = -TURN_SPEED;
      } else {
        ship.rotation = 0;
      }
      
      // Thrust ship
      if (keyDown['ArrowUp'] || keyDown['w']) {
        ship.thrusting = true;
        const thrustMultiplier = ship.hasSpeedBoost ? SHIP_BOOST_MULTIPLIER : 1;
        ship.thrust.x += SHIP_THRUST * thrustMultiplier * Math.cos(ship.angle);
        ship.thrust.y -= SHIP_THRUST * thrustMultiplier * Math.sin(ship.angle);
      } else {
        ship.thrusting = false;
      }
      
      // Fire bullet - continuous firing when space is held down
      if (keyDown[' '] && !gameOver) {
        const currentTime = Date.now();
        // Check if enough time has passed since the last shot
        if (currentTime - ship.lastFireTime > FIRE_RATE) {
          if (bullets.length < MAX_BULLETS) {
            fireBullet();
            ship.lastFireTime = currentTime;
          }
        }
      }
      
      // Note: R key for restart is now handled directly in the keydown event handler
      
      // Update ship angle
      ship.angle += ship.rotation;
      
      // Update ship position
      ship.x += ship.thrust.x;
      ship.y += ship.thrust.y;
      
      // Apply friction
      ship.thrust.x *= FRICTION;
      ship.thrust.y *= FRICTION;
      
      // Handle edge of screen
      if (ship.x < 0) ship.x = canvas.width;
      if (ship.x > canvas.width) ship.x = 0;
      if (ship.y < 0) ship.y = canvas.height;
      if (ship.y > canvas.height) ship.y = 0;
    };
    
    // Update bullets
    const updateBullets = () => {
      for (let i = bullets.length - 1; i >= 0; i--) {
        // Move the bullet
        bullets[i].x += bullets[i].xv;
        bullets[i].y += bullets[i].yv;
        
        // Increment age
        bullets[i].age++;
        
        // Remove old bullets
        if (bullets[i].age > BULLET_LIFE) {
          bullets.splice(i, 1);
          continue;
        }
        
        // Handle edge of screen
        if (bullets[i].x < 0) bullets[i].x = canvas.width;
        if (bullets[i].x > canvas.width) bullets[i].x = 0;
        if (bullets[i].y < 0) bullets[i].y = canvas.height;
        if (bullets[i].y > canvas.height) bullets[i].y = 0;
      }
    };
    
    // Update saucer bullets
    const updateSaucerBullets = () => {
      for (let i = saucerBullets.length - 1; i >= 0; i--) {
        // Move the bullet
        saucerBullets[i].x += saucerBullets[i].xv;
        saucerBullets[i].y += saucerBullets[i].yv;
        
        // Increment age
        saucerBullets[i].age++;
        
        // Remove old bullets
        if (saucerBullets[i].age > BULLET_LIFE) {
          saucerBullets.splice(i, 1);
          continue;
        }
        
        // Handle edge of screen
        if (saucerBullets[i].x < 0 || saucerBullets[i].x > canvas.width ||
            saucerBullets[i].y < 0 || saucerBullets[i].y > canvas.height) {
          saucerBullets.splice(i, 1);
        }
      }
    };
    
    // Update saucers
    const updateSaucers = () => {
      for (let i = saucers.length - 1; i >= 0; i--) {
        const saucer = saucers[i];
        
        // Move the saucer
        saucer.x += saucer.xv;
        saucer.y += saucer.yv;
        
        // Check if saucer should fire
        if (Date.now() - saucer.lastShot > SAUCER_FIRE_RATE) {
          saucerFire(saucer);
        }
        
        // Remove if off screen for too long
        if (saucer.x < -50 || saucer.x > canvas.width + 50 ||
            saucer.y < -50 || saucer.y > canvas.height + 50) {
          saucers.splice(i, 1);
        }
      }
    };
    
    // Update asteroids
    const updateAsteroids = () => {
      for (let i = 0; i < asteroids.length; i++) {
        // Move the asteroid
        asteroids[i].x += asteroids[i].xv;
        asteroids[i].y += asteroids[i].yv;
        
        // Handle edge of screen
        if (asteroids[i].x < 0 - asteroids[i].radius) asteroids[i].x = canvas.width + asteroids[i].radius;
        if (asteroids[i].x > canvas.width + asteroids[i].radius) asteroids[i].x = 0 - asteroids[i].radius;
        if (asteroids[i].y < 0 - asteroids[i].radius) asteroids[i].y = canvas.height + asteroids[i].radius;
        if (asteroids[i].y > canvas.height + asteroids[i].radius) asteroids[i].y = 0 - asteroids[i].radius;
      }
    };
    
    // Update floating texts
    const updateFloatingTexts = () => {
      floatingTexts.forEach(text => {
        // Update position
        text.x += text.speed;
        if (text.x > canvas.width) {
          text.x = -ctx.measureText(text.text).width;
          text.y = Math.random() * (canvas.height - 100) + 50;
          text.alpha = 0;
        }
        
        // Fade in
        if (text.x < canvas.width / 4) {
          text.alpha = Math.min(1, text.alpha + 0.02);
        }
        // Fade out
        else if (text.x > canvas.width * 3/4) {
          text.alpha = Math.max(0, text.alpha - 0.02);
        }
      });
    };

    // Draw floating texts
    const drawFloatingTexts = () => {
      floatingTexts.forEach(text => {
        if (text.alpha > 0) {
          ctx.font = `${text.size}px VT323, monospace`;
          ctx.fillStyle = text.color;
          ctx.globalAlpha = text.alpha;
          ctx.fillText(text.text, text.x, text.y);
          ctx.globalAlpha = 1;
        }
      });
    };
    
    // Game loop
    const gameLoop = () => {
      if (gameOver) return;
      
      updateFloatingTexts();
      // Clear canvas
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      updateShip();
      updateBullets();
      updateSaucerBullets();
      updateSaucers();
      updateAsteroids();
      checkCollisions();
      
      drawShip();
      drawAsteroids();
      drawBullets();
      drawSaucerBullets();
      drawSaucers();
      drawPowerups();
      drawHUD();
      drawFloatingTexts();
      
      if (gameOver) {
        drawGameOver();
      }
      
      requestAnimationFrame(gameLoop);
    };
    
    // Initialize game
    const init = () => {
      createAsteroids();
      resetShip();
      gameLoop();
    };
    
    // Start game
    init();
    
    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameStarted]);
  
  // Draw the game if started, otherwise show start screen
  useEffect(() => {
    if (!gameStarted) {
      drawStartScreen();
      
      // Redraw start screen on window resize
      const handleResize = () => {
        drawStartScreen();
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [gameStarted]);
  
  return (
    <div className="asteroids-container">
      <canvas ref={canvasRef} className="asteroids-canvas"></canvas>
      {/* Only show instructions during gameplay if needed, removed as per request */}
    </div>
  );
};

export default AsteroidsGame; 